import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, ArrowLeft, Clock, Lightbulb } from "lucide-react";

export default function PlaybookDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/playbooks/${slug}`).then((r) => setP(r.data)).catch(() => setError(true));
  }, [slug]);

  if (error) return (
    <div className="min-h-screen bg-[#FAFBFD]"><Nav />
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-slate-900">Playbook not found</h1>
        <Link to="/playbooks" className="mt-4 inline-block"><Button variant="outline" className="rounded-full">Back to playbooks</Button></Link>
      </div>
    </div>
  );

  if (!p) return <div className="min-h-screen bg-[#FAFBFD]"><Nav /><div className="p-12 text-center text-slate-500">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-[#FAFBFD]">
      <Nav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/playbooks" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="playbook-back-link">
          <ArrowLeft className="w-4 h-4" /> All playbooks
        </Link>

        <div className="mt-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight" data-testid="playbook-detail-name">{p.name}</h1>
            <p className="text-slate-500 mt-1">{p.category}</p>
          </div>
          <Badge variant="outline" className="rounded-full text-xs bg-blue-50 text-blue-700 border-blue-200 flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> Typical response: {p.typical_response_days} days
          </Badge>
        </div>

        <Card className="trust-card mt-8">
          <CardContent className="p-6 sm:p-8">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500" /> Refund tactics for {p.name}
            </h2>
            <ul className="mt-4 space-y-3" data-testid="playbook-detail-tips">
              {p.tips.map((t, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-1 shrink-0 w-6 h-6 rounded-full bg-[#EEF4FF] text-[#1E3A8A] text-xs font-bold grid place-items-center">{i+1}</span>
                  <span className="text-slate-700 text-sm sm:text-base">{t}</span>
                </li>
              ))}
            </ul>
            {p.support_url && (
              <a href={p.support_url} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-1 text-sm text-[#1E3A8A] hover:underline" data-testid="playbook-support-link">
                Contact {p.name} support <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </CardContent>
        </Card>

        <Card className="trust-card mt-6">
          <CardContent className="p-6 sm:p-8 text-center">
            <h3 className="text-xl font-semibold text-slate-900">Ready to open a case for {p.name}?</h3>
            <p className="text-slate-600 mt-2 text-sm">4 messages, evidence checklist, tracker — $19 flat.</p>
            <Link to="/auth"><Button className="mt-4 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-12 px-6" data-testid="playbook-detail-start-case">Start my case</Button></Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
